import React from 'react';
import styled from 'styled-components';
import { EColors, TStyledTheme } from 'theme';

export type ButtonProps = React.HTMLProps<HTMLButtonElement> & {
    height?: number;
    width?: string | number;
    secondary?: boolean;
    small?: boolean;
    noBorder?: boolean;
    warning?: boolean;
    variant?: string;
    buttonColor?: EColors;
};

export const buttonColorsStyledFn = ({
    warning,
    secondary,
    noBorder,
    theme: { getColor, EColors },
    variant,
    buttonColor = 'primaryAction',
}: ButtonProps & { theme: TStyledTheme }) => {
    const { pureWhite, disabledAction } = EColors;

    const actionColor = warning ? EColors.warningAction : EColors[buttonColor];

    const primaryStyles = {
        base: {
            backgroundColor: getColor(actionColor),
            color: getColor(pureWhite),
            border: `1px solid transparent`,
            whiteSpace: 'nowrap',
        },
        hover: {
            backgroundColor: getColor(actionColor, 0.8),
        },
        disabled: {
            backgroundColor: getColor(disabledAction),
            borderColor: getColor(disabledAction),
            color: getColor(pureWhite),
        },
    } as const; // due to whiteSpace property needing to be 'nowrap' not string;

    const secondaryStyles = {
        base: {
            backgroundColor: variant === 'outlined' ? 'transparent' : getColor(pureWhite),
            color: getColor(actionColor),
            border: `1px solid ${getColor(actionColor, 0.2)}`,
            whiteSpace: 'nowrap',
        },
        hover: primaryStyles.base,
        disabled: {
            backgroundColor: getColor(pureWhite),
            color: getColor(disabledAction),
            borderColor: getColor(disabledAction),
        },
    } as const;

    const styles = secondary ? secondaryStyles : primaryStyles;

    return {
        ...styles.base,
        ...(noBorder ? { borderWidth: 0 } : {}),
        '&:hover': styles.hover,
        '&:focus': styles.hover,
        '&:disabled': styles.disabled,
    };
};

const Button = styled('button')<ButtonProps>`
    border-radius: 3px;
    cursor: pointer;
    font-size: 15px;
    font-weight: 600;
    line-height: 1;
    height: ${({ height, small }) => height || (small ? 28 : 36)}px;
    width: ${({ width }) => (width !== undefined ? (typeof width === 'number' ? width + 'px' : width) : '94px')};
    outline: none;
    &:disabled {
        cursor: unset;
        pointer-events: none;
    }
    ${buttonColorsStyledFn}
    transition: 0.2s all ease-out;
`;

export default Button;

export const ContentWidthButton = styled(Button)`
    width: fit-content;
`;
